import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineFileUpload } from "react-icons/md";
import {
    addFileLink,
    discardTempFile,
    removeFileLink,
    type UploadOptions,
} from "./api/file";
import { resolveLabwickUploadUrl } from "./utils/pictureUtils";
import { ActionTypes } from "@/actions";
import { Button } from "@/components/ui/button";
import { useAuth } from "./auth/AuthContext";
import FilePickerDialog from "./component/FilePickerDialog";

interface FileFieldProps {
    filename: string;
    label?: string;
    parent?: any;
    onChange?: (data: { filename: string }) => void;
    collection?: string;
    fieldName?: string;
    /** When true, upload goes to temp folder; finalize on save moves to collection folder */
    temporaryUpload?: boolean;
}

const FileField = ({ filename, parent, onChange, collection, fieldName, temporaryUpload }: FileFieldProps) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [linking, setLinking] = useState(false);
    const dispatch = useDispatch();
    const { user: authUser } = useAuth();
    const reduxUser = useSelector((state: any) => state.user);
    const user = authUser || reduxUser;
    const _currentItem = useSelector((state: any) => state.item.current);
    const slug = useSelector((state: any) => state.item.currentSlug);
    const currentCollection = useSelector((state: any) => state.item.currentCollection);

    const effectiveCollection = collection || currentCollection || slug;
    const itemUid = parent?.uid ?? (parent as any)?._id ?? (parent as any)?.id;

    const buildUploadOptions = (): UploadOptions => {
        const opts: UploadOptions = {};
        if (effectiveCollection) opts.collection = effectiveCollection;
        if (temporaryUpload === true) {
            opts.temporary = true;
        } else if (itemUid) {
            opts.uid = String(itemUid);
        }
        if (fieldName) opts.field = fieldName;
        const uploaderUid = user?.uid || user?.info?.uid;
        if (uploaderUid) opts.userId = uploaderUid;
        return opts;
    };

    const unlinkCurrent = async (name: string) => {
        if (!name || name.startsWith("temp/")) {
            if (name.startsWith("temp/")) {
                await discardTempFile(name).catch(() => null);
            }
            return;
        }
        if (effectiveCollection && itemUid && fieldName) {
            await removeFileLink({
                collection: effectiveCollection,
                itemUid: String(itemUid),
                field: fieldName,
                filename: name,
            }).catch(() => null);
        }
    };

    const linkSelected = async (name: string) => {
        if (temporaryUpload || !name || name.startsWith("temp/")) return;
        if (!effectiveCollection || !itemUid || !fieldName) return;
        await addFileLink({
            collection: effectiveCollection,
            itemUid: String(itemUid),
            field: fieldName,
            filename: name,
        }).catch(() => null);
    };

    const handleSelect = async (data: { filename: string }) => {
        const nextName = (data.filename || "").trim();
        if (!nextName) return;

        if (parent?.uid && parent.uid !== _currentItem?.uid) {
            dispatch({
                type: ActionTypes.EDIT_ITEM_START,
                payload: { slug, data: parent, collection: effectiveCollection || slug },
            });
        }

        setLinking(true);
        try {
            const previousName = (filename || "").trim();
            if (previousName && previousName !== nextName) {
                await unlinkCurrent(previousName);
            }

            await linkSelected(nextName);
            onChange?.({ filename: nextName });
        } finally {
            setLinking(false);
        }
    };

    const fileUrl = filename ? resolveLabwickUploadUrl(filename) : "";
    const busy = linking;

    if (filename && filename !== "") {
        return (
            <>
                <div className="flex items-center gap-2 flex-wrap">
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate max-w-[200px]"
                    >
                        {filename.split("/").pop() || filename}
                    </a>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPickerOpen(true)}
                        disabled={busy}
                    >
                        {busy ? "Linking..." : "Change"}
                    </Button>
                </div>
                <FilePickerDialog
                    open={pickerOpen}
                    onOpenChange={setPickerOpen}
                    onSelect={handleSelect}
                    uploadOptions={buildUploadOptions()}
                />
            </>
        );
    }

    return (
        <>
            <div
                className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                onClick={busy ? undefined : () => setPickerOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (!busy && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        setPickerOpen(true);
                    }
                }}
                title="Choose or upload a file (PDF, TXT or DOCX)"
            >
                {busy ? (
                    <span className="animate-spin rounded-full h-5 w-5 border-2 border-muted-foreground border-t-transparent flex-shrink-0" />
                ) : (
                    <MdOutlineFileUpload className="text-2xl text-muted-foreground flex-shrink-0" />
                )}
                <span className="text-sm text-muted-foreground hover:text-foreground">
                    {busy ? "Linking..." : "Choose file (PDF, TXT, DOCX)"}
                </span>
            </div>
            <FilePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onSelect={handleSelect}
                uploadOptions={buildUploadOptions()}
            />
        </>
    );
};

export default FileField;
